import { createSlice } from '@reduxjs/toolkit'
import { RootState } from 'store'
import {
  getUser,
  updateUser,
  deleteUserImage,
  deleteUser,
  getUserPrivileges,
  addNewPrivilege,
  updatePrivilege,
  cancelPrivilege,
  getUserStats,
  getUserExtra,
  getUserGameStats,
  getUserLinkedAccounts,
  getUserRounds,
} from './thunks'
import { forgotPassword } from 'store/authentication/thunks'
import { deleteGame, deleteGamePlayer, deleteGameRound, updateGame } from 'store/game/thunks'
import { mapCoreUserToUser, mapUpdatedCoreUserToUser } from './utils'
import { DEFAULT_USER_AVATAR } from 'config'

const user: User = {
  id: null,
  active: false,
  account: null,
  email: null,
  fullName: null,
  firstName: null,
  lastName: null,
  profileDescription: null,
  regDate: null,
  phone: null,
  handicap: null,
  gender: null,
  dateOfBirth: null,
  temporary: false,
  country: null,
  club: null,
}

const initialState: UserState = {
  user: user,
  userRounds: [],
  userPrivileges: [],
  userTransactions: [],
  userStats: [],
  userExtra: null,
  userGameStats: null,
  userLinkedAccounts: [],
  error: null,
  status: 'idle',
  updatingError: null,
  updatingStatus: 'idle',
  deletingImageStatus: 'idle',
  deletingImageError: null,
  resetingPasswordStatus: 'idle',
  resetingPasswordError: null,
  deletingUserStatus: 'idle',
  deletingUserError: null,
  userRoundsStatus: 'idle',
  userRoundsError: null,
  userPrivilegesStatus: 'idle',
  userPrivilegesError: null,
  privilegeStatus: 'idle',
  privilegeError: null,
  cancelPrivilegeStatus: 'idle',
  cancelPrivilegeError: null,
  userTransactionsStatus: 'idle',
  userTransactionsError: null,
  userStatsStatus: 'idle',
  userStatsError: null,
  userExtraStatus: 'idle',
  userExtraError: null,
  userGameStatsStatus: 'idle',
  userGameStatsError: null,
  userLinkedAccountsStatus: 'idle',
  userLinkedAccountsError: null,
  roundsCursor: null,
}

const slice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearUser: (state) => {
      state.user = user
      state.userRounds = []
      state.userPrivileges = []
      state.userTransactions = []
      state.userStats = []
      state.userExtra = null
    },
    clearPrivilege: (state) => {
      state.privilegeStatus = 'idle'
      state.privilegeError = null
      state.cancelPrivilegeStatus = 'idle'
      state.cancelPrivilegeError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUser.pending, (state) => {
        state.status = 'loading'
        state.error = null
        state.updatingStatus = 'idle'
        state.deletingImageStatus = 'idle'
        state.resetingPasswordStatus = 'idle'
        state.deletingUserStatus = 'idle'
        state.userRoundsStatus = 'idle'
        state.privilegeStatus = 'idle'
        state.userStatsStatus = 'idle'
        state.userExtraStatus = 'idle'
        state.roundsCursor = null
        state.userRounds = []
      })
      .addCase(getUser.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getUser.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.user = mapCoreUserToUser(action.payload)
      })

    builder
      .addCase(updateUser.pending, (state) => {
        state.updatingStatus = 'loading'
        state.updatingError = null
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.updatingStatus = 'failed'
        state.updatingError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.updatingStatus = 'succeeded'
        const updatedUser = mapUpdatedCoreUserToUser(action.payload)
        state.user = { ...state.user, ...updatedUser }
      })

    builder
      .addCase(deleteUserImage.pending, (state) => {
        state.deletingImageStatus = 'loading'
        state.deletingImageError = null
      })
      .addCase(deleteUserImage.rejected, (state, action) => {
        state.deletingImageStatus = 'failed'
        state.deletingImageError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deleteUserImage.fulfilled, (state, action) => {
        state.deletingImageStatus = 'succeeded'
        const imageType = action.meta.arg.imageType
        if (imageType === 'avatar') {
          state.user.avatarUrl = DEFAULT_USER_AVATAR
        } else {
          state.user.coverImgUrl = ''
        }
      })

    builder
      .addCase(forgotPassword.pending, (state) => {
        state.resetingPasswordStatus = 'loading'
        state.resetingPasswordError = null
      })
      .addCase(forgotPassword.rejected, (state, action) => {
        state.resetingPasswordStatus = 'failed'
        state.resetingPasswordError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(forgotPassword.fulfilled, (state) => {
        state.resetingPasswordStatus = 'succeeded'
      })

    builder
      .addCase(deleteUser.pending, (state) => {
        state.deletingUserStatus = 'loading'
        state.deletingUserError = null
      })
      .addCase(deleteUser.rejected, (state, action) => {
        state.deletingUserStatus = 'failed'
        state.deletingUserError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(deleteUser.fulfilled, (state) => {
        state.deletingUserStatus = 'succeeded'
      })

    builder
      .addCase(getUserRounds.pending, (state) => {
        state.userRoundsStatus = 'loading'
        state.userRoundsError = null
      })
      .addCase(getUserRounds.rejected, (state, action) => {
        state.userRoundsStatus = 'failed'
        state.userRoundsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getUserRounds.fulfilled, (state, action) => {
        state.userRoundsStatus = 'succeeded'
        const { data, paging } = action.payload
        if (state.user.id === action.meta.arg.id) {
          state.roundsCursor = paging?.next ? paging.next : null
          state.userRounds = action.meta.arg.params?.cursor ? state.userRounds.concat(data) : data
        }
      })

    builder
      .addCase(getUserPrivileges.pending, (state) => {
        state.userPrivilegesStatus = 'loading'
        state.userPrivilegesError = null
      })
      .addCase(getUserPrivileges.rejected, (state, action) => {
        state.userPrivilegesStatus = 'failed'
        state.userPrivilegesError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getUserPrivileges.fulfilled, (state, action) => {
        state.userPrivilegesStatus = 'succeeded'
        state.userPrivileges = action.payload
        state.user.privileges = state.userPrivileges
      })

    builder
      .addCase(addNewPrivilege.pending, (state) => {
        state.privilegeStatus = 'loading'
        state.privilegeError = null
      })
      .addCase(addNewPrivilege.rejected, (state, action) => {
        state.privilegeStatus = 'failed'
        state.privilegeError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(addNewPrivilege.fulfilled, (state, action) => {
        state.privilegeStatus = 'succeeded'
        state.userPrivileges = [...state.userPrivileges, action.payload]
        state.user.privileges = state.userPrivileges
      })

    builder
      .addCase(updatePrivilege.pending, (state) => {
        state.privilegeStatus = 'loading'
        state.privilegeError = null
      })
      .addCase(updatePrivilege.rejected, (state, action) => {
        state.privilegeStatus = 'failed'
        state.privilegeError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(updatePrivilege.fulfilled, (state, action) => {
        state.privilegeStatus = 'succeeded'
        const { id } = action.meta.arg
        const index = state.userPrivileges.map((privilege: UserPrivilege) => privilege.id).indexOf(id)
        state.userPrivileges[index] = action.payload
        state.user.privileges = state.userPrivileges
      })

    builder
      .addCase(cancelPrivilege.pending, (state) => {
        state.cancelPrivilegeStatus = 'loading'
        state.cancelPrivilegeError = null
      })
      .addCase(cancelPrivilege.rejected, (state, action) => {
        state.cancelPrivilegeStatus = 'failed'
        state.cancelPrivilegeError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(cancelPrivilege.fulfilled, (state, action) => {
        state.cancelPrivilegeStatus = 'succeeded'
        const { id } = action.meta.arg
        const index = state.userPrivileges.map((privilege: UserPrivilege) => privilege.id).indexOf(id)
        state.userPrivileges[index] = action.payload
        state.user.privileges = state.userPrivileges
      })

    builder
      .addCase(getUserStats.pending, (state) => {
        state.userStatsStatus = 'loading'
        state.userStatsError = null
      })
      .addCase(getUserStats.rejected, (state, action) => {
        state.userStatsStatus = 'failed'
        state.userStatsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getUserStats.fulfilled, (state, action) => {
        state.userStatsStatus = 'succeeded'
        state.userStats = action.payload.data
      })

    builder
      .addCase(getUserExtra.pending, (state) => {
        state.userExtraStatus = 'loading'
        state.userExtraError = null
      })
      .addCase(getUserExtra.rejected, (state, action) => {
        state.userExtraStatus = 'failed'
        state.userExtraError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getUserExtra.fulfilled, (state, action) => {
        state.userExtraStatus = 'succeeded'
        state.userExtra = action.payload.data
      })

    builder
      .addCase(getUserGameStats.pending, (state) => {
        state.userGameStatsStatus = 'loading'
        state.userGameStatsError = null
      })
      .addCase(getUserGameStats.rejected, (state, action) => {
        state.userGameStatsStatus = 'failed'
        state.userGameStatsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getUserGameStats.fulfilled, (state, action) => {
        state.userGameStatsStatus = 'succeeded'
        state.userGameStats = action.payload.data
      })

    builder
      .addCase(getUserLinkedAccounts.pending, (state) => {
        state.userLinkedAccountsStatus = 'loading'
        state.userLinkedAccountsError = null
      })
      .addCase(getUserLinkedAccounts.rejected, (state, action) => {
        state.userLinkedAccountsStatus = 'failed'
        state.userLinkedAccountsError = action?.payload?.message || 'Something went wrong. Please try again.'
      })
      .addCase(getUserLinkedAccounts.fulfilled, (state, action) => {
        state.userLinkedAccountsStatus = 'succeeded'
        state.userLinkedAccounts = action.payload.data ?? []
      })

    builder.addCase(deleteGame.fulfilled, (state, action) => {
      const gameId = typeof action.meta.arg === 'string' ? parseInt(action.meta.arg) : action.meta.arg
      const currentIndex = state.userRounds.map((round: UserRound) => round.eventId).indexOf(gameId)
      const indexIsValid = [undefined, -1].includes(currentIndex) === false
      if (indexIsValid) {
        state.userRounds.splice(currentIndex, 1)
      }
    })

    builder.addCase(deleteGameRound.fulfilled, (state, action) => {
      const roundId = action.meta.arg.roundId
      const currentIndex = state.userRounds.map((round: UserRound) => round.roundId).indexOf(roundId)
      const indexIsValid = [undefined, -1].includes(currentIndex) === false
      if (indexIsValid) {
        state.userRounds.splice(currentIndex, 1)
      }
    })

    builder.addCase(deleteGamePlayer.fulfilled, (state, action) => {
      const { gameId, playerId } = action.meta.arg
      const gameIndex = state.userRounds.map((round: UserRound) => round.eventId).indexOf(gameId)

      const indexIsValid = [undefined, -1].includes(gameIndex) === false
      // If deleted player is current user, delete the round from store
      if (indexIsValid && playerId === state.user.id) {
        state.userRounds.splice(gameIndex, 1)
      } else if (indexIsValid) {
        const playerIndex = state.userRounds[gameIndex].withPlayers
          .map((player: RoundPlayer) => player.profileId?.toString())
          .indexOf(playerId.toString())

        if (playerIndex !== undefined) {
          state.userRounds[gameIndex].withPlayers.splice(playerIndex, 1)
        }
      }
    })

    builder.addCase(updateGame.fulfilled, (state, action) => {
      const { data } = action.meta.arg

      if (data.rounds.length > 0) {
        const roundId = data.rounds[0].id ? Number(data.rounds[0].id) : 0
        const roundIndex = state.userRounds.map((round: UserRound) => round.roundId).indexOf(roundId)
        const indexIsValid = [undefined, -1].includes(roundIndex) === false
        if (indexIsValid) {
          state.userRounds[roundIndex].gameCompleted = data.rounds[0].isCompleted || false
        }
      }
    })
  },
})

export const { clearUser, clearPrivilege } = slice.actions

export const selectUser = (state: RootState): UserState => state.user

export default slice.reducer
